import { Accordion, AccordionItem } from "@heroui/react";
import {
    createFileRoute,
    useNavigate,
    useRouterState,
    Outlet,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { Resource } from "@/types/user";

export const Route = createFileRoute("/dashboard/fields/$fieldId/setup")({
    component: RouteComponent,
});

const SETUP_KEYS = [
    "field",
    "crop-zones",
    "soil-management",
    "management-zones",
    "irrigation-system",
    "chemigation-systems",
    "fertilizer-preferences",
    "treatment-sectors",
] as const;

const accordionItemClassNames = {
    content: "pt-0",
    trigger: "py-2",
};

function stripQuery(path: string) {
    return path.split("?")[0].split("#")[0];
}

function RouteComponent() {
    const { t } = useTranslation("setup");
    const { isAtLeastDetail } = useAuth();
    const { cropZonesEnabled } = useFeatureFlag();
    const navigate = useNavigate();
    const { location } = useRouterState();
    const { fieldId } = Route.useParams();
    const visibleKeys = useMemo(() => {
        let keys: readonly string[] = SETUP_KEYS;
        if (!cropZonesEnabled) {
            keys = keys.filter((k) => k !== "crop-zones");
        }
        if (!isAtLeastDetail(Resource.TREATMENT_SECTORS)) {
            keys = keys.filter((k) => k !== "treatment-sectors");
        }
        return keys;
    }, [cropZonesEnabled, isAtLeastDetail]);

    const basePath = `/dashboard/fields/${fieldId}/setup`;

    const currentKey = useMemo<string | null>(() => {
        if (!location.pathname.startsWith(basePath)) return null;
        const remainder = stripQuery(location.pathname.slice(basePath.length));
        const seg = (remainder.startsWith("/") ? remainder.slice(1) : remainder)
            .split("/")[0]
            .trim();
        return (visibleKeys as readonly string[]).includes(seg) ? seg : null;
    }, [location.pathname, basePath, visibleKeys]);

    useEffect(() => {
        const remainder = stripQuery(location.pathname.slice(basePath.length));
        const seg = (remainder.startsWith("/") ? remainder.slice(1) : remainder)
            .split("/")[0]
            .trim();
        if (
            (seg === "treatment-sectors" &&
                !isAtLeastDetail(Resource.TREATMENT_SECTORS)) ||
            (seg === "crop-zones" && !cropZonesEnabled)
        ) {
            navigate({
                to: "/dashboard/fields/$fieldId/setup",
                params: { fieldId },
                replace: true,
            });
        }
    }, [
        location.pathname,
        basePath,
        isAtLeastDetail,
        cropZonesEnabled,
        navigate,
        fieldId,
    ]);

    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
        () => new Set(currentKey ? [currentKey] : []),
    );
    useEffect(() => {
        setSelectedKeys(new Set(currentKey ? [currentKey] : []));
    }, [currentKey]);

    function onSelectionChange(next: "all" | Set<React.Key>) {
        if (next === "all") return;
        const first = Array.from(next)[0] as string | undefined;

        if (first) {
            navigate({
                to: "/dashboard/fields/$fieldId/setup/" + first,
                params: { fieldId },
            });
        } else {
            navigate({
                to: "/dashboard/fields/$fieldId/setup",
                params: { fieldId },
            });
        }
        setSelectedKeys(next as Set<string>);
    }

    return (
        <Accordion
            selectionMode="single"
            selectedKeys={selectedKeys}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onSelectionChange={onSelectionChange as any}
        >
            <AccordionItem
                key="field"
                title={t("field_management.title")}
                classNames={accordionItemClassNames}
            >
                {currentKey === "field" ? <Outlet /> : null}
            </AccordionItem>

            <AccordionItem
                key="soil-management"
                title={t("soil_management.title")}
                classNames={accordionItemClassNames}
            >
                {currentKey === "soil-management" ? <Outlet /> : null}
            </AccordionItem>

            <AccordionItem
                key="management-zones"
                title={t("management_zones.title")}
                classNames={accordionItemClassNames}
            >
                {currentKey === "management-zones" ? <Outlet /> : null}
            </AccordionItem>

            <AccordionItem
                key="irrigation-system"
                title={t("irrigation_system.title")}
                classNames={accordionItemClassNames}
            >
                {currentKey === "irrigation-system" ? <Outlet /> : null}
            </AccordionItem>

            <AccordionItem
                key="chemigation-systems"
                title={t("chemigation_systems.title")}
                classNames={accordionItemClassNames}
            >
                {currentKey === "chemigation-systems" ? <Outlet /> : null}
            </AccordionItem>

            <AccordionItem
                key="fertilizer-preferences"
                title={t("fertilizer_preferences.title")}
                classNames={accordionItemClassNames}
            >
                {currentKey === "fertilizer-preferences" ? <Outlet /> : null}
            </AccordionItem>

            {cropZonesEnabled ? (
                <AccordionItem
                    key="crop-zones"
                    title={t("crop_zones.title")}
                    classNames={accordionItemClassNames}
                >
                    {currentKey === "crop-zones" ? <Outlet /> : null}
                </AccordionItem>
            ) : null}

            {isAtLeastDetail(Resource.TREATMENT_SECTORS) ? (
                <AccordionItem
                    key="treatment-sectors"
                    title={t("treatment_sectors.title")}
                    classNames={accordionItemClassNames}
                >
                    {currentKey === "treatment-sectors" ? <Outlet /> : null}
                </AccordionItem>
            ) : null}
        </Accordion>
    );
}
